import { useState } from "react";
import { toast } from "sonner";
import { CheckIcon, FileTextIcon } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Etiqueta } from "@/components/ui/etiqueta";
import { FiltroSegmentado } from "@/components/ui/filtro-segmentado";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { CorreccionDeLaDuda } from "@/components/generacion/CorreccionPendiente";
import { Input } from "@/components/ui/input";
import { useResponderDuda } from "@/hooks/useGeneracion";
import { useReleerLaRedaccion } from "@/components/generacion/redaccionAutomatica";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { formatearFechaYHora } from "@/lib/fechas";
import {
  ETIQUETA_GRAVEDAD,
  ETIQUETA_TIPO_DE_DUDA,
  FILTROS_DE_DUDAS,
  TONO_DE_GRAVEDAD,
  TONO_DE_TIPO_DE_DUDA,
  conteoDeDudas,
  etiquetasDeLosHechosAfectados,
  filtrarDudas,
  siguienteDudaAbierta,
  textoDeLaRespuesta,
  type Duda,
  type FiltroDeDudas,
  type Hecho,
  type RespuestaAUnaDuda,
} from "@/lib/generacion";
import { urlDeArchivoDeAntecedente } from "@/lib/informes";
import { cn } from "@/lib/utils";

interface PanelDeDudasProps {
  informeId: number;
  dudas: Duda[];
  hechos: Hecho[];
  filtro: FiltroDeDudas;
  dudaId: number | null;
  onFiltro: (filtro: FiltroDeDudas) => void;
  onElegir: (dudaId: number) => void;
  /** Lleva a la fase de valorización; sin él la nota se muestra sin el botón. */
  onIrALaValorizacion?: () => void;
}

/**
 * Las dudas del análisis: la lista a la izquierda (o arriba, en pantallas
 * angostas) y el detalle de la elegida, donde se responde.
 */
export function PanelDeDudas({
  informeId,
  dudas,
  hechos,
  filtro,
  dudaId,
  onFiltro,
  onElegir,
  onIrALaValorizacion,
}: PanelDeDudasProps) {
  const visibles = filtrarDudas(dudas, filtro);
  const conteo = conteoDeDudas(dudas);
  // Lo elegido puede no estar en el filtro (se contestó y salió de "Abiertas"):
  // se sigue mostrando mientras no se elija otra cosa.
  const elegida = dudas.find((d) => d.id === dudaId) ?? visibles[0] ?? null;

  return (
    <div className="grid items-start gap-3.5 lg:grid-cols-[22.5rem_minmax(0,1fr)]">
      <section
        aria-label="Lista de dudas"
        className="bg-card flex flex-col gap-2 rounded-[1.25rem] border-[1.5px] p-3"
      >
        <FiltroSegmentado
          aria-label="Filtrar las dudas"
          className="w-full [&_[data-slot=filtro-segmentado-opcion]]:flex-1 [&_[data-slot=filtro-segmentado-opcion]]:justify-center [&_[data-slot=filtro-segmentado-opcion]]:px-1.5"
          valor={filtro}
          onCambio={onFiltro}
          opciones={FILTROS_DE_DUDAS.map(({ filtro: f, etiqueta }) => ({
            valor: f,
            etiqueta,
            cantidad: conteo[f],
          }))}
        />

        <div className="flex max-h-72 flex-col gap-1 overflow-y-auto lg:max-h-none">
          {visibles.length === 0 ? (
            <p className="text-meta text-ink-medio px-3 py-6 text-center">No hay dudas en este filtro.</p>
          ) : (
            visibles.map((d) => <FilaDeDuda key={d.id} duda={d} elegida={d.id === elegida?.id} onElegir={onElegir} />)
          )}
        </div>
      </section>

      {elegida ? (
        <DetalleDeDuda
          // Cada duda arranca con su formulario limpio.
          key={elegida.id}
          informeId={informeId}
          duda={elegida}
          dudas={dudas}
          hechos={hechos}
          onElegir={onElegir}
          onIrALaValorizacion={onIrALaValorizacion}
        />
      ) : null}
    </div>
  );
}

function FilaDeDuda({ duda, elegida, onElegir }: { duda: Duda; elegida: boolean; onElegir: (id: number) => void }) {
  const respondida = duda.respuesta !== null;
  const yaNoAparece = duda.estado === "ya_no_aparece";

  return (
    <button
      type="button"
      aria-current={elegida ? "true" : undefined}
      onClick={() => onElegir(duda.id)}
      className={cn(
        "flex items-start gap-2.5 rounded-xl border-[1.5px] px-3 py-2.5 text-left transition-colors",
        elegida ? "bg-divisor border-input" : "hover:bg-superficie-tenue border-transparent",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-1.5 size-2 shrink-0 rounded-full",
          yaNoAparece
            ? "bg-ink-ghost"
            : respondida
              ? "bg-success-dot"
              : duda.gravedad === "bloquea"
                ? "bg-danger-dot"
                : "bg-warning-dot",
        )}
      />
      <span className="flex min-w-0 flex-col gap-1.5">
        <span
          className={cn(
            "text-[0.78125rem] leading-snug font-bold",
            respondida || yaNoAparece ? "text-ink-muted" : "text-ink",
          )}
        >
          {duda.corto}
        </span>
        <span className="flex flex-wrap items-center gap-1">
          <Etiqueta size="sm" tono={TONO_DE_TIPO_DE_DUDA[duda.tipo]}>
            {ETIQUETA_TIPO_DE_DUDA[duda.tipo]}
          </Etiqueta>
          <InsigniaDeGravedad gravedad={duda.gravedad} size="sm" />
          {yaNoAparece ? (
            <Etiqueta size="sm" tono="tenue">
              Ya no aparece
            </Etiqueta>
          ) : null}
          {respondida ? (
            <Etiqueta size="sm" tono="success">
              Respondida
            </Etiqueta>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function InsigniaDeGravedad({ gravedad, size }: { gravedad: Duda["gravedad"]; size?: "sm" | "md" }) {
  return (
    <Etiqueta size={size} tono={TONO_DE_GRAVEDAD[gravedad]}>
      {ETIQUETA_GRAVEDAD[gravedad]}
    </Etiqueta>
  );
}

/* ---------------------------------------------------------------------------
   Detalle
--------------------------------------------------------------------------- */

interface DetalleDeDudaProps {
  informeId: number;
  duda: Duda;
  dudas: Duda[];
  hechos: Hecho[];
  onElegir: (id: number) => void;
  onIrALaValorizacion?: () => void;
}

function DetalleDeDuda({ informeId, duda, dudas, hechos, onElegir, onIrALaValorizacion }: DetalleDeDudaProps) {
  const [cambiando, setCambiando] = useState(false);
  const afectados = etiquetasDeLosHechosAfectados(duda.hechos_afectados, hechos);
  const posicion = dudas.findIndex((d) => d.id === duda.id) + 1;
  const respondida = duda.respuesta !== null;
  const editable = duda.vigente;

  return (
    <section
      aria-label="Detalle de la duda"
      className="bg-card flex flex-col gap-4 rounded-[1.25rem] border-[1.5px] px-[1.375rem] py-5"
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Etiqueta tono={TONO_DE_TIPO_DE_DUDA[duda.tipo]}>{ETIQUETA_TIPO_DE_DUDA[duda.tipo]}</Etiqueta>
          <InsigniaDeGravedad gravedad={duda.gravedad} />
          <span className="text-ink-medio ml-auto font-mono text-[0.6875rem] font-bold">
            Duda {posicion} de {dudas.length}
          </span>
        </div>
        <h3 className="text-ink text-[1.0625rem] leading-snug font-extrabold tracking-[-0.015em] text-pretty">
          {duda.pregunta}
        </h3>
        {afectados.length > 0 ? (
          <p className="text-ink-muted text-xs font-semibold">Afecta a: {afectados.join(", ")}</p>
        ) : null}
      </div>

      {duda.evidencia.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span className="text-ink text-xs font-bold">Evidencia</span>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-2.5">
            {duda.evidencia.map((e, i) => (
              <div
                key={`${e.archivo_id ?? e.documento}-${i}`}
                className="bg-superficie-tenue border-border flex flex-col gap-1.5 rounded-[0.875rem] border-[1.5px] px-3.5 py-3"
              >
                <div className="flex items-center gap-2">
                  <FileTextIcon aria-hidden className="text-ink-hint size-3.5 shrink-0" />
                  <span className="text-ink min-w-0 truncate text-xs font-bold">{e.documento}</span>
                  {e.ubicacion ? (
                    <span className="text-ink-medio ml-auto font-mono text-[0.65625rem] font-bold">{e.ubicacion}</span>
                  ) : null}
                </div>
                {e.fragmento ? (
                  <p className="text-ink-secundario font-mono text-xs leading-[1.6] text-pretty">{e.fragmento}</p>
                ) : null}
                {e.archivo_id !== null ? (
                  <a
                    href={urlDeArchivoDeAntecedente(informeId, e.archivo_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-success-soft-foreground w-fit text-[0.71875rem] font-bold hover:underline"
                  >
                    Abrir documento
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!editable ? (
        <>
          <Alert>
            <AlertDescription>
              Esta duda ya no aparece: el último análisis no la volvió a levantar. Queda a la vista, sin poder
              responderse
              {respondida ? ", y su respuesta se conserva" : ""}.
            </AlertDescription>
          </Alert>
          {respondida ? <RespuestaGuardada duda={duda} /> : null}
          {respondida && duda.requiere_revisar_valorizacion ? (
            <AvisoDeValorizacion onIr={onIrALaValorizacion} />
          ) : null}
        </>
      ) : respondida && !cambiando ? (
        <>
          <RespuestaGuardada duda={duda} onCambiar={() => setCambiando(true)} />
          {duda.correccion ? (
            <CorreccionDeLaDuda
              informeId={informeId}
              duda={duda}
              correccion={duda.correccion}
              onIrALaValorizacion={onIrALaValorizacion}
            />
          ) : null}
          {duda.requiere_revisar_valorizacion && !duda.correccion ? (
            <AvisoDeValorizacion onIr={onIrALaValorizacion} />
          ) : null}
        </>
      ) : (
        <FormularioDeRespuesta
          informeId={informeId}
          duda={duda}
          dudas={dudas}
          onElegir={onElegir}
          onCancelar={respondida ? () => setCambiando(false) : undefined}
          alTerminarDeCambiar={() => setCambiando(false)}
        />
      )}
    </section>
  );
}

/** La respuesta no fijó ningún dato: la valorización no se rehace sola. */
function AvisoDeValorizacion({ onIr }: { onIr?: () => void }) {
  return (
    <div className="bg-warning-soft flex flex-wrap items-center gap-3 rounded-[0.875rem] px-4 py-2.5">
      <span className="text-warning-soft-foreground min-w-0 flex-1 text-xs font-bold">
        Puede requerir ajustar la valorización
      </span>
      {onIr ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-auto rounded-[0.625rem] px-3 py-1.5"
          onClick={onIr}
        >
          Ir a la valorización
        </Button>
      ) : null}
    </div>
  );
}

function RespuestaGuardada({ duda, onCambiar }: { duda: Duda; onCambiar?: () => void }) {
  const r = duda.respuesta!;
  const cuando = r.respondida_en ? formatearFechaYHora(r.respondida_en) : null;
  const quien = r.respondida_por?.nombre ?? null;
  const meta = [quien ? `Respondida por ${quien}` : "Respondida", cuando, "queda en el respaldo del informe"]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="bg-success-soft flex items-start gap-3 rounded-[0.875rem] px-4 py-3.5">
      <CheckIcon aria-hidden className="text-success-soft-foreground mt-0.5 size-4 shrink-0" strokeWidth={2.4} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-ink text-[0.84375rem] font-bold">{textoDeLaRespuesta(duda)}</span>
        <span className="text-success-soft-foreground text-xs font-semibold">{meta}</span>
      </div>
      {onCambiar ? (
        <button
          type="button"
          onClick={onCambiar}
          className="bg-card text-ink rounded-[0.625rem] px-3 py-1.5 text-xs font-bold"
        >
          Cambiar respuesta
        </button>
      ) : null}
    </div>
  );
}

/** Un `id` válido para el campo de un dato: la cita puede llevar espacios y arrobas. */
const idDelDato = (dudaId: number, cita: string) => `duda-${dudaId}-dato-${cita.replace(/\W+/g, "_")}`;

type Seleccion = number | "propia" | "no_aplica" | null;

function seleccionInicial(duda: Duda): { seleccion: Seleccion; texto: string } {
  const r = duda.respuesta;
  if (!r) return { seleccion: null, texto: "" };
  if (r.tipo === "opcion") return { seleccion: r.opcion, texto: "" };
  if (r.tipo === "propia") return { seleccion: "propia", texto: r.texto ?? "" };

  return { seleccion: "no_aplica", texto: "" };
}

interface FormularioDeRespuestaProps {
  informeId: number;
  duda: Duda;
  dudas: Duda[];
  onElegir: (id: number) => void;
  /** Solo al CAMBIAR una respuesta ya dada: vuelve a mostrarla sin tocar nada. */
  onCancelar?: () => void;
  alTerminarDeCambiar: () => void;
}

function FormularioDeRespuesta({
  informeId,
  duda,
  dudas,
  onElegir,
  onCancelar,
  alTerminarDeCambiar,
}: FormularioDeRespuestaProps) {
  const inicial = seleccionInicial(duda);
  const [seleccion, setSeleccion] = useState<Seleccion>(inicial.seleccion);
  const [texto, setTexto] = useState(inicial.texto);
  const responder = useResponderDuda(informeId);
  // Responder la última duda arranca la redacción en el servidor: hay que releerla.
  const releerLaRedaccion = useReleerLaRedaccion(informeId);
  const eraRespondida = duda.respuesta !== null;

  // El valor de cada dato extraíble que la duda afecta (T5): se ofrece con una respuesta
  // propia o con una opción que no fija su propio valor.
  const datosAEscribir = duda.hechos_a_escribir ?? [];
  const [valores, setValores] = useState<Record<string, string>>(() =>
    Object.fromEntries(datosAEscribir.map((h) => [h.cita, h.valor_escrito ?? ""])),
  );
  const opcionElegida = typeof seleccion === "number" ? duda.opciones.find((o) => o.indice === seleccion) : undefined;
  const admiteValores =
    datosAEscribir.length > 0 && (seleccion === "propia" || (opcionElegida !== undefined && !opcionElegida.fija_un_hecho));
  const hechos = admiteValores
    ? datosAEscribir.flatMap((h) => ((valores[h.cita] ?? "").trim() === "" ? [] : [{ cita: h.cita, valor: valores[h.cita].trim() }]))
    : [];
  const conHechos = hechos.length > 0 ? { hechos } : {};

  const respuesta: RespuestaAUnaDuda | null =
    seleccion === null
      ? null
      : seleccion === "no_aplica"
        ? { tipo: "no_aplica" }
        : seleccion === "propia"
          ? texto.trim() === ""
            ? null
            : { tipo: "propia", texto: texto.trim(), ...conHechos }
          : { tipo: "opcion", opcion: seleccion, ...conHechos };

  function guardar() {
    if (!respuesta) return;

    responder.mutate(
      { dudaId: duda.id, respuesta },
      {
        onSuccess: () => {
          releerLaRedaccion();
          if (eraRespondida) {
            // Cambiar una respuesta no manda a otra duda: se queda donde estaba.
            alTerminarDeCambiar();
            toast.success("Respuesta cambiada");
            return;
          }
          // Responder SÍ avanza a la siguiente abierta (la lista aún no se
          // releyó, por eso se excluye esta a mano).
          const siguiente = siguienteDudaAbierta(
            dudas.map((d) => (d.id === duda.id ? { ...d, estado: "respondida" as const } : d)),
            duda.id,
          );
          if (siguiente) onElegir(siguiente.id);
          toast.success(siguiente ? "Respuesta guardada · siguiente duda" : "Respuesta guardada");
        },
        onError: (error) => {
          toast.error(
            error instanceof ApiError && error.status === 409
              ? "Esta duda ya no es vigente: el análisis cambió. Recargamos la lista."
              : getApiErrorMessage(error, "No pudimos guardar la respuesta. Revisa los valores escritos e inténtalo de nuevo."),
          );
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <span className="text-ink text-xs font-bold" id={`respuesta-${duda.id}`}>
        Su respuesta
      </span>

      <RadioGroup
        aria-labelledby={`respuesta-${duda.id}`}
        value={seleccion === null ? "" : String(seleccion)}
        onValueChange={(v) =>
          setSeleccion(v === "propia" || v === "no_aplica" ? v : Number(v))
        }
      >
        {duda.opciones.map((o) => (
          <div key={o.indice} className="flex flex-col gap-1">
            <OpcionDeRespuesta id={`duda-${duda.id}-opcion-${o.indice}`} valor={String(o.indice)} etiqueta={o.etiqueta} elegida={seleccion === o.indice} />
            {o.fija_un_hecho && o.valor_formateado ? (
              <p className="text-ink-medio pl-11 text-xs font-semibold">
                Esta opción fija {o.valor_formateado} en la hoja de hechos.
              </p>
            ) : null}
            {o.corrige_a ? (
              <p className="text-ink-medio pl-11 text-xs font-semibold">
                Esta opción deja pendiente corregir {o.corrige_campo ?? "el dato"} a {o.corrige_a}: no lo cambia, lo
                corriges en su pestaña.
              </p>
            ) : null}
          </div>
        ))}
        <OpcionDeRespuesta id={`duda-${duda.id}-propia`} valor="propia" etiqueta="Escribir otra respuesta" elegida={seleccion === "propia"} />
        <OpcionDeRespuesta id={`duda-${duda.id}-no-aplica`} valor="no_aplica" etiqueta="No aplica" elegida={seleccion === "no_aplica"} />
      </RadioGroup>

      {seleccion === "propia" ? (
        <Textarea
          aria-label="Su respuesta escrita"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribe la respuesta que quedará en el respaldo del informe"
          rows={3}
        />
      ) : null}

      {admiteValores ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-ink text-xs font-bold">Valor de los datos que esta duda afecta (opcional)</legend>
          {datosAEscribir.map((h) => (
            <div key={h.cita} className="flex flex-col gap-1">
              <Label htmlFor={idDelDato(duda.id, h.cita)} className="text-ink-body text-xs font-semibold">
                {h.campo}
                {h.valor_actual ? ` · hoy: ${h.valor_actual}` : ""}
              </Label>
              <Input
                id={idDelDato(duda.id, h.cita)}
                value={valores[h.cita] ?? ""}
                onChange={(e) => setValores((v) => ({ ...v, [h.cita]: e.target.value }))}
                placeholder="Déjalo vacío si esta respuesta no lo fija"
              />
            </div>
          ))}
          <p className="text-ink-medio text-xs font-semibold">
            Lo que escribas queda como dato del tasador y alimenta la redacción.
          </p>
        </fieldset>
      ) : null}

      <div className="border-divisor-fuerte flex flex-wrap items-center gap-3 border-t-[1.5px] pt-3.5">
        <p className="text-ink-muted min-w-0 flex-1 text-xs font-semibold text-pretty">
          La respuesta queda registrada con tu nombre y la hora: es parte del respaldo del informe.
        </p>
        {onCancelar ? (
          <Button
            type="button"
            variant="outline"
            className="border-input h-auto rounded-xl px-4 py-2.5 text-[0.8125rem] shadow-none"
            onClick={onCancelar}
          >
            Cancelar
          </Button>
        ) : null}
        <Button
          type="button"
          variant="fuerte"
          className="h-auto rounded-xl px-[1.125rem] py-2.5 text-[0.8125rem]"
          disabled={respuesta === null || responder.isPending}
          onClick={guardar}
        >
          Guardar respuesta
        </Button>
      </div>
    </div>
  );
}

function OpcionDeRespuesta({ id, valor, etiqueta, elegida }: { id: string; valor: string; etiqueta: string; elegida: boolean }) {
  return (
    <Label
      htmlFor={id}
      className={cn(
        "hover:border-primary flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-2.5 text-[0.8125rem]",
        elegida ? "border-primary bg-success-soft font-bold" : "border-border bg-card font-semibold",
      )}
    >
      <RadioGroupItem id={id} value={valor} />
      {etiqueta}
    </Label>
  );
}
